import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormBuilder } from '@tf/formbuilder'
import { FormWrapper } from 'shared/formBuilder/formWrapper'
import { AccountAddressFormDE_AT } from 'shared/formBuilder/forms/customerInformationFormDE_AT'
import { getLanguageLocale, getMarket, CircleSpinner } from 'shared/index'
import { migrateService } from 'selfServicePortal/services/MigrationServices/migrationService'
import updateCustomerService from 'selfServicePortal/services/CustomerServices/updateCustomerService'
import {
	ICustomer,
	IUpdateCustomer,
} from 'selfServicePortal/interfaces/i-api-customer'
import migrationAppMockup from '../../../img/migration-app-mockup.png'
import migrationCardMockup from '../../../img/migration-card-mockup.png'
import { getStoreBadges } from './storeBadges'

import './MigrationModal.scss'

type MigrationPage = 'info' | 'addressEdit' | 'confirmation' | 'error'

interface MigrationModalProps {
	show: boolean
	onClose: () => void
	customerData: ICustomer
	initialPage?: MigrationPage
	showBackButton?: boolean
	onAddressUpdated?: () => Promise<void>
}

export function MigrationModal({
	show,
	onClose,
	customerData,
	initialPage,
	showBackButton = true,
	onAddressUpdated,
}: MigrationModalProps) {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const [page, setPage] = useState<MigrationPage>(initialPage ?? 'info')
	const { appStore: appStoreBadge, googlePlay: googlePlayBadge } =
		getStoreBadges(i18n.language)

	const migrateMutation = useMutation({
		mutationFn: () => migrateService(),
		onSuccess: () => setPage('confirmation'),
		onError: () => setPage('error'),
	})

	const addressUpdateMutation = useMutation({
		mutationFn: async (updateData: IUpdateCustomer) => {
			await updateCustomerService(updateData, t)
			try {
				await onAddressUpdated?.()
			} catch {
				
			}
		},
		onSuccess: () => setPage('info'),
	})

	const handleAccept = () => {	
		migrateMutation.mutate()
	}

	const handleAddressSubmit = ({ customer }: any) => {
		const updateData: IUpdateCustomer = {
			customerId: customerData.customerId,
			bankId: customerData.bankId,
			street: customer.street,
			houseNumber: customer.houseNumber,
			city: customer.city,
			postalCode: customer.postalCode,
			apartmentNumber: customer.apartmentNumber || '',
			shortAddress: `${customer.street} ${customer.houseNumber}`,
			email: customerData.email,
			mobilePhoneNumber: customerData.mobilePhoneNumber,
		}
		addressUpdateMutation.mutate(updateData)
	}

	const addressFormSchema = FormWrapper(
		[AccountAddressFormDE_AT(customerData)],
		{
			formName: 'migrationAddress',
			blockName: 'address',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
			submitButtonText: t('MigrationConfirmAddress'),
		},
	)

	const renderInfoPage = () => (
		<>
			<div className="migration-modal-header">
				<h5 className="migration-modal-title">
					{t('MigrationModalTitle')}
				</h5>
				<button
					type="button"
					className="btn-close-modal"
					onClick={onClose}
					aria-label="Close">
					<FontAwesomeIcon icon="times" />
				</button>
			</div>
			<Modal.Body>
				<p className="migration-description">
					{t('MigrationModalDescription')}
				</p>
				<div className="migration-preview-images">
					<div className="preview-container">
						<img src={migrationAppMockup} alt="New TF Bank app" />
					</div>
					<div className="preview-container">
						<img src={migrationCardMockup} alt="New TF Bank card" />
					</div>
				</div>
				<div className="new-card-section">
					<h5>{t('MigrationModalNewCardTitle')}</h5>
					<p>{t('MigrationModalNewCardText')}</p>
				</div>
				<div className="delivery-address-header">
					<span>{t('MigrationModalDeliveryAddress')}</span>
					<button
						type="button"
						className="btn-edit-address"
						onClick={() => setPage('addressEdit')}
						disabled={migrateMutation.isLoading}>
						{t('MigrationModalEditAddress')}
					</button>
				</div>
				<div className="delivery-address-box">
					<span className="address-label">
						{t('MigrationModalAddress')}
					</span>
					<span className="address-value">
						{customerData.street} {customerData.houseNumber}
					</span>
				</div>
				<div className="delivery-address-header">
					<span>{t('MigrationModalEmail')}</span>
				</div>
				<div className="email-address-box">
					<span className="email-label">
						{t('MigrationModalEmail')}
					</span>
					<span className="email-value">{customerData.email}</span>
				</div>
				<p className="email-disclaimer">
					{t('MigrationModalEmailDisclaimer')}
				</p>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn btn-primary w-100"
					onClick={handleAccept}
					disabled={migrateMutation.isLoading}>
					{t('MigrationModalAccept')}
				</button>
			</Modal.Footer>
			{migrateMutation.isLoading && (
				<div className="modal-loading-overlay">
					<CircleSpinner />
				</div>
			)}
		</>
	)

	const renderAddressEditPage = () => (
		<>
			<div className="migration-modal-header migration-modal-header--address">
				<button
					type="button"
					className="btn-back-arrow"
					onClick={() => {
						addressUpdateMutation.reset()
						setPage('info')
					}}
					disabled={addressUpdateMutation.isLoading}
					aria-label="Back">
					<FontAwesomeIcon icon="arrow-left" />
				</button>
				<h5 className="migration-modal-title">
					{t('MigrationModalEditAddressTitle')}
				</h5>
				<span className="header-spacer" aria-hidden="true" />
			</div>
			<Modal.Body>
				<p>{t('MigrationModalEditAddressDescription')}</p>
				<div className="address-form-wrapper">
					{addressUpdateMutation.isLoading && (
						<div className="address-form-loading">
							<CircleSpinner />
						</div>
					)}
					<FormBuilder
						formSchema={addressFormSchema}
						apiError={undefined}
						showErrorsInForm={true}
						showSubmitButton={true}
						onSubmit={handleAddressSubmit}
					/>
				</div>
				{addressUpdateMutation.isError && (
					<div className="error mt-2">
						{t('MigrationModalErrorText')}
					</div>
				)}
			</Modal.Body>
		</>
	)

	const renderConfirmationPage = () => (
		<>
			<div className="migration-modal-header">
				<h5 className="migration-modal-title">
					{t('MigrationModalConfirmationTitle')}
				</h5>
			</div>
			<Modal.Body>
				<p>{t('MigrationModalConfirmationText')}</p>
				<div className="app-download-links">
					<a
						href="https://apps.apple.com/it/app/tf-card/id6745559029"
						target="_blank"
						rel="noopener noreferrer"
						className="store-link">
						<img
							src={appStoreBadge}
							alt="Download on the App Store"
						/>
					</a>
					<a
						href="https://play.google.com/store/apps/details?id=se.tfbank.tfbankmobile"
						target="_blank"
						rel="noopener noreferrer"
						className="store-link">
						<img
							src={googlePlayBadge}
							alt="Get it on Google Play"
						/>
					</a>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<a
					href={t('MigrationModalMyPagesLink')}
					target="_blank"
					rel="noopener noreferrer"
					className="btn btn-mypages w-50">
					{t('MigrationModalDownloadApp')}
				</a>
				<button
					type="button"
					className="btn btn-primary w-50"
					onClick={() => navigate('/logout')}>
					{t('Logout')}
				</button>
			</Modal.Footer>
		</>
	)

	const renderErrorPage = () => (
		<>
			<div className="migration-modal-header">
				<h5 className="migration-modal-title">
					{t('MigrationModalErrorTitle')}
				</h5>
				<button
					type="button"
					className="btn-close-modal"
					onClick={onClose}
					aria-label="Close">
					<FontAwesomeIcon icon="times" />
				</button>
			</div>
			<Modal.Body>
				<p>{t('MigrationModalErrorText')}</p>
			</Modal.Body>
			<Modal.Footer>
				<div className="migration-actions">
					<button
						type="button"
						className="btn btn-primary w-100"
						onClick={() => {
							migrateMutation.reset()
							setPage('info')
						}}>
						{t('MigrationModalTryAgain')}
					</button>
					<button
						type="button"
						className="btn btn-outline-primary w-100"
						onClick={onClose}>
						{t('Close')}
					</button>
				</div>
			</Modal.Footer>
		</>
	)

	const renderPage = () => {
		switch (page) {
			case 'info':
				return renderInfoPage()
			case 'addressEdit':
				return renderAddressEditPage()
			case 'confirmation':
				return renderConfirmationPage()
			case 'error':
				return renderErrorPage()
		}
	}

	return (
		<Modal
			show={show}
			onHide={onClose}
			className="migration-modal"
			backdrop="static"
			keyboard={false}
			animation={false}
			centered
			container={document.getElementById('root')}>
			{renderPage()}
		</Modal>
	)
}
